if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.addEventListener('click', (event) => {
        const panel = this.querySelector('.drawer__inner');
        if (!panel || panel.contains(event.target)) return;
        this.close();
      });
      this.setHeaderCartIconAccessibility();
    }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.removeAttribute('aria-hidden');
      document.body.classList.add('cart-drawer-open');
      document.getElementById('CartBackdrop')?.classList.add('is-active');
    });

    // Pause Lenis smooth-scroll so the page behind can't scroll while the
    // drawer is open. Lenis hijacks wheel/touch with JS, so native
    // overflow:hidden alone can't lock it. (window.lenis is undefined when
    // prefers-reduced-motion disables Lenis — the ?. keeps this safe.)
    window.lenis?.stop();

    // Set up focus trap after drawer becomes visible.
    // Use a timeout fallback so focus is always trapped even when
    // prefers-reduced-motion disables transitions (transitionend may not fire).
    const setupTrap = () => {
      const emptyEl = this.querySelector('.drawer__inner-empty');
      const containerToTrapFocusOn = (this.classList.contains('is-empty') && emptyEl)
        ? emptyEl
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    const trapTimer = setTimeout(setupTrap, 400);
    this.addEventListener(
      'transitionend',
      () => {
        clearTimeout(trapTimer);
        setupTrap();
      },
      { once: true }
    );

  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.getElementById('CartBackdrop')?.classList.remove('is-active');
    removeTrapFocus(this.activeElement);

    // Resume Lenis smooth-scroll now the drawer is closed.
    window.lenis?.start();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Sync is-empty class with live cart state so CSS + focus-trap stay correct
    if (parsedState.items && parsedState.items.length > 0) {
      this.classList.remove('is-empty');
    } else {
      this.classList.add('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const liveRegion = document.getElementById('cart-count-live-region');
    if (liveRegion) {
      const count = parsedState.item_count ?? 0;
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = count === 0
          ? window.cartStrings?.empty ?? 'Your cart is empty'
          : window.cartStrings?.itemCount?.replace('[count]', count) ?? `Cart updated: ${count} item${count !== 1 ? 's' : ''}`;
      });
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  }

  customElements.define('cart-drawer', CartDrawer);

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);

/* ─── Quantity +/- button handler ───────────────────────────────────────────
   Listens for clicks on [data-qty-action] buttons inside the cart drawer and
   updates the associated number input, then fires a `change` event so that
   CartItems picks up the update via its existing debounced onChange listener.
────────────────────────────────────────────────────────────────────────── */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-action]');
    if (!btn) return;
    // Only act inside the cart drawer
    if (!btn.closest('cart-drawer')) return;

    const targetId = btn.dataset.qtyTarget;
    const input = document.getElementById(targetId);
    if (!input) return;

    const action = btn.dataset.qtyAction;
    const current = parseInt(input.value, 10) || 0;
    const min = parseInt(input.min, 10) || 0;
    const max = parseInt(input.max, 10);
    const step = parseInt(input.step, 10) || 1;

    if (action === 'decrease' && current > min) {
      input.value = Math.max(min, current - step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (action === 'increase' && (!max || current < max)) {
      input.value = max ? Math.min(max, current + step) : current + step;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

/* ─── Cart drawer recommendations (JS fallback) ──────────────────────────────
   When no metafield upsell is set, the template renders a
   `.cart__upsell-recommendations[data-product-id]` placeholder. On drawer
   open we fetch Shopify's recommendations API and populate that slot with
   the first result not already in the cart.
────────────────────────────────────────────────────────────────────────── */
function loadCartDrawerRecommendations(drawerEl) {
  const zone = drawerEl.querySelector('.cart__upsell-recommendations[data-product-id]');
  if (!zone || zone.dataset.loaded) return;

  const productId = zone.dataset.productId;
  if (!productId) return;

  const cartIds = (zone.dataset.cartIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const currencyCode = zone.dataset.currency || 'USD';

  fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=related`)
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then(({ products }) => {
      const eligible = products.filter((p) => !cartIds.includes(String(p.id)));
      const product = eligible[0];
      if (!product) return;

      let formattedPrice = '';
      try {
        formattedPrice = new Intl.NumberFormat(
          document.documentElement.lang || 'en',
          { style: 'currency', currency: currencyCode }
        ).format(product.price / 100);
      } catch (_) {
        formattedPrice = (product.price / 100).toFixed(2);
      }

      const imgSrc = product.featured_image
        ? product.featured_image.replace(/\.([a-z]+)(\?.*)?$/, '_160x160.$1$2')
        : '';

      const variantId = product.variants && product.variants[0] ? product.variants[0].id : '';
      const addToBag = zone.dataset.addToBag || 'Add to bag';
      const addShort = zone.dataset.addShort || 'Add';
      const safeTitle = product.title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      zone.setAttribute('aria-hidden', 'false');
      zone.innerHTML = [
        `<quick-add-card class="quick-add-card quick-add-card--single" data-variant-id="${variantId}">`,
        imgSrc
          ? `<img src="${imgSrc}" alt="${safeTitle}" class="quick-add-card__image" width="56" height="56" loading="lazy">`
          : '',
        '<div class="quick-add-card__info">',
        `<p class="quick-add-card__title title-one">${safeTitle}</p>`,
        '</div>',
        `<button type="button" class="quick-add-card__btn button button--secondary" aria-label="${addToBag}: ${safeTitle}">`,
        `<span class="quick-add-card__btn-text--desktop">${addToBag}</span>`,
        `<span class="quick-add-card__btn-text--mobile">${addShort}</span>`,
        '</button>',
        '</quick-add-card>',
      ].join('');

      zone.dataset.loaded = 'true';
    })
    .catch(() => {
      // Silently hide the placeholder on failure
      if (zone.parentElement) zone.parentElement.style.display = 'none';
    });
}

// Trigger recommendations load whenever the drawer opens
  const _cartDrawerProto = CartDrawer.prototype;
  const _origOpen = _cartDrawerProto.open;
  _cartDrawerProto.open = function (...args) {
    _origOpen.apply(this, args);
    loadCartDrawerRecommendations(this);
  };
}

/* ─── Cart Upsell Card ───────────────────────────────────────────────────────
   Custom element powering the cart upsell block.
   - Variant thumbnails (.cart-upsell-card__variant-btn) let the user pick
     which product (treated as a variant) to add.
   - Clicking "Add" POSTs the selected variant to the cart and re-renders
     the drawer via CartDrawer.renderContents.
────────────────────────────────────────────────────────────────────────── */
if (!customElements.get('cart-upsell-card')) {
  customElements.define(
    'cart-upsell-card',
    class CartUpsellCard extends HTMLElement {
      connectedCallback() {
        this.addBtn = this.querySelector('.cart-upsell-card__add-btn');
        this.addLabel = this.querySelector('.cart-upsell-card__btn-label');
        this.variantBtns = [...this.querySelectorAll('.cart-upsell-card__variant-btn')];
        this.priceSpan = this.querySelector('.cart-upsell-card__btn-price');
        this.separator = this.querySelector('.cart-upsell-card__btn-separator');
        this.mainImg = this.querySelector('.cart-upsell-card__main-img');
        this.variantsContainer = this.querySelector('.cart-upsell-card__variants');

        this.variantBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.selectVariant(btn));
        });

        if (this.addBtn && !this.addBtn.dataset.choose) {
          this.addBtn.addEventListener('click', () => this.onAdd());
        }

        // Fetch variant data from GIDs if present
        const gidsStr = this.dataset.productGids;
        if (gidsStr && this.variantsContainer) {
          this.loadVariantsFromGids(gidsStr.split(','));
        }

        const activeVariantBtn =
          this.variantBtns.find((btn) => btn.classList.contains('is-active')) || this.variantBtns[0];

        if (activeVariantBtn) {
          this.selectVariant(activeVariantBtn);
        } else {
          this.updateButtonState(
            this.addBtn?.dataset.variantAvailable !== 'false',
            this.priceSpan?.textContent?.trim() || ''
          );
        }
      }

      loadVariantsFromGids(gids) {
        // Extract product IDs from GID strings like "gid://shopify/Product/15566471921994"
        const productIds = gids
          .map((gid) => {
            const match = gid.trim().match(/\/(\d+)$/);
            return match ? match[1] : null;
          })
          .filter(Boolean);

        if (productIds.length === 0) return;

        // Fetch all products in parallel
        Promise.all(productIds.map((id) => this.fetchProductData(id)))
          .then((products) => this.renderVariantCircles(products))
          .catch((err) => console.error('[cart-upsell-card] Failed to load variants:', err));
      }

      fetchProductData(productId) {
        return fetch(`/products/${productId}.json`)
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then(({ product }) => product);
      }

      renderVariantCircles(products) {
        if (!products || products.length === 0) return;

        const container = this.variantsContainer;
        if (!container) return;

        // Clear any existing buttons
        container.innerHTML = '';

        const selectedId = parseInt(this.dataset.selectedVariantId, 10);
        const currentProductId = products.find((p) => p.variants.some((v) => v.id === selectedId))?.id;

        products.forEach((product) => {
          const variantDetails = this.extractVariantDetails(product);
          if (!variantDetails || !variantDetails.image) return;

          const variant = product.selected_or_first_available_variant || product.variants[0];
          if (!variant) return;

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `cart-upsell-card__variant-btn${currentProductId === product.id ? ' is-active' : ''}`;
          btn.dataset.variantId = variant.id;
          btn.dataset.formattedPrice = this.formatPrice(variant.price);
          btn.dataset.available = variant.available;
          btn.setAttribute('aria-label', variantDetails.name || product.title);
          btn.setAttribute('aria-pressed', currentProductId === product.id ? 'true' : 'false');

          if (product.featured_image) {
            btn.dataset.imageSrc = product.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_160x160.$1$2');
            btn.dataset.imageSrcset = `${product.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_100x100.$1$2')} 100w, ${product.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_200x200.$1$2')} 200w`;
            btn.dataset.imageAlt = product.featured_image.alt || product.title;
          }

          const img = document.createElement('img');
          img.src = variantDetails.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_28x28.jpg');
          img.srcSet = `${variantDetails.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_28x28.jpg')} 1x, ${variantDetails.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/, '_56x56.jpg')} 2x`;
          img.alt = variantDetails.name || product.title;
          img.width = 14;
          img.height = 14;
          img.loading = 'lazy';

          btn.appendChild(img);
          btn.addEventListener('click', () => this.selectVariant(btn));
          container.appendChild(btn);
        });

        // Re-cache variant buttons
        this.variantBtns = [...this.querySelectorAll('.cart-upsell-card__variant-btn')];
      }

      extractVariantDetails(product) {
        // Try to find variant_details metafield on this product
        if (!product.metafields) return null;

        const variantMeta = product.metafields.find(
          (m) => m.namespace === 'custom' && m.key === 'variant_details'
        );

        if (!variantMeta || !variantMeta.value) return null;

        try {
          const details = typeof variantMeta.value === 'string' ? JSON.parse(variantMeta.value) : variantMeta.value;
          return {
            name: details.variant_name || product.title,
            image: details.variant_image,
          };
        } catch (e) {
          console.error('[cart-upsell-card] Error parsing variant details:', e);
          return null;
        }
      }

      formatPrice(priceInCents) {
        const amount = priceInCents / 100;
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: (Shopify?.currency?.active || 'USD'),
        }).format(amount);
      }

      selectVariant(btn) {
        this.variantBtns.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');

        this.dataset.selectedVariantId = btn.dataset.variantId;

        if (this.priceSpan && btn.dataset.formattedPrice) {
          this.priceSpan.textContent = btn.dataset.formattedPrice;
        }

        if (this.mainImg && btn.dataset.imageSrc) {
          this.mainImg.src = btn.dataset.imageSrc;
          if (btn.dataset.imageSrcset) this.mainImg.srcset = btn.dataset.imageSrcset;
          if (btn.dataset.imageAlt) this.mainImg.alt = btn.dataset.imageAlt;
        }

        this.updateButtonState(btn.dataset.available !== 'false', btn.dataset.formattedPrice || '');
      }

      updateButtonState(isAvailable, formattedPrice) {
        if (!this.addBtn || this.addBtn.dataset.choose) return;

        const addLabel = this.addBtn.dataset.addLabel || 'Add';
        const soldOutLabel = this.addBtn.dataset.soldOutLabel || 'Sold out';

        this.addBtn.dataset.variantAvailable = isAvailable ? 'true' : 'false';

        if (this.addLabel) {
          this.addLabel.textContent = isAvailable ? addLabel : soldOutLabel;
        }

        if (isAvailable) {
          this.addBtn.removeAttribute('aria-disabled');
        } else {
          this.addBtn.setAttribute('aria-disabled', 'true');
        }

        if (this.separator) {
          this.separator.hidden = !isAvailable || !formattedPrice;
        }

        if (this.priceSpan) {
          if ('hidden' in this.priceSpan) {
            this.priceSpan.hidden = !isAvailable || !formattedPrice;
          }
          this.priceSpan.textContent = isAvailable ? formattedPrice : '';
        }
      }

      onAdd() {
        const variantId = this.dataset.selectedVariantId;
        if (!variantId || this.addBtn.getAttribute('aria-disabled') === 'true') return;

        this.addBtn.setAttribute('aria-disabled', 'true');
        this.addBtn.classList.add('loading');

        const cartEl =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');
        const sections = cartEl ? cartEl.getSectionsToRender().map((s) => s.id) : [];

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1,
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              console.error(
                '[cart-upsell-card] Error adding to cart:',
                response.description || response.message
              );
              return;
            }
            if (cartEl) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-upsell-card',
                productVariantId: variantId,
                cartData: response,
              });
              cartEl.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((err) => console.error('[cart-upsell-card] Fetch error:', err))
          .finally(() => {
            this.addBtn.removeAttribute('aria-disabled');
            this.addBtn.classList.remove('loading');
          });
      }
    }
  );
}
